import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  IEventExceptionReasons,
  IUtilisationEvent,
  IUtilisationEventResponse,
  IUtilisationSnapshotResponse,
  IUtilisationStatisticsResponse,
  IVehicleUtilisationSnapshot,
  ReferenceObjectTypes,
  UtilisationChangeTypes,
  VehicleStatus,
  IVehicleActivityDetail,
  IScheduledActivityDetail,
} from '../models/vehicle-status.model';
import { RestProvider } from './rest.service';
import { environment } from '../../environments/environment';
import { IQueryParameters } from '../models/api-base-actions.interface';
import colors from '../constants/colors.constants';

@Injectable({
  providedIn: 'root',
})
export class VehicleStatusService {
  private baseApiUrl = environment.dataliveApiUrl;
  constructor(private rest: RestProvider) { }


  getUtilisationChangeTypeName(type: number): string {
    let typeName = '';
    switch (type) {
      case 0:
        typeName = UtilisationChangeTypes.UTILIZED
        break;
      case 1:
        typeName = UtilisationChangeTypes.UNUTILIZED
        break;
      case 3:
        typeName = UtilisationChangeTypes.UNVOR
        break;
      default:
        typeName = UtilisationChangeTypes.NO_CHANGE
        break;
    }
    return typeName;
  }

  getReferenceObjectTypeName(type: string): string {
    // DEFECT = "DF", "Defect"
    // SAFETY_INSPECTION = "SI", "Safety Inspection"
    // VEHICLE_CHECK = "VC", "Vehicle Check"
    // TRIGGER_EVENT = "TE", "Trigger Event"
    // VEHICLE_ACTIVITY = "VA", "Vehicle Activity"
    // # This is partially for users to be able to add
    // # Custom events and for carrying over the
    // # previous day's status to the coming day
    // MANUAL = "MN", "Manual"
    let typeName = '';
    switch (type) {
      case 'DF':
        typeName = ReferenceObjectTypes.DEFECT;
        break;
      case 'SI':
        typeName = ReferenceObjectTypes.SAFETY_INSPECTION;
        break;
      case 'VC':
        typeName = ReferenceObjectTypes.VEHICLE_CHECK;
        break;
      case 'TE':
        typeName = ReferenceObjectTypes.TRIGGER_EVENT;
        break;
      case 'VA':
        typeName = ReferenceObjectTypes.VEHICLE_ACTIVITY;
        break;
      default:
        typeName = ReferenceObjectTypes.MANUAL
        break;
    }
    return typeName;
  }

  getUtilisationStatus(snapshot: IVehicleUtilisationSnapshot) {
    if (!snapshot.is_utilized && !snapshot.is_vor && !snapshot.manually_marked_as_utilized) {
      return 'Available'; // Available
    } else if (snapshot.is_vor || !snapshot.is_utilized) {
      return 'Unused'; // Unused
    } else if (snapshot.is_utilized) {
      return 'Used'; // Used
    } else {
      return 'Unused'; // Unused
    }
  }

  getUtilisationBadgeColour(utilisationStatus: boolean, vor: boolean) {
    if (!utilisationStatus && !vor) {
      return 'warning'; // Available
    } else if (vor || !utilisationStatus) {
      return 'danger'; // Unused
    } else if (utilisationStatus) {
      return 'success'; // Used
    } else {
      return 'danger'; // Unused
    }
  }

  getVehicleUtilisationSnapshots(queryParams?: IQueryParameters) {
    const _queryParams = queryParams ? queryParams : null
    return this.rest.get<IUtilisationSnapshotResponse>(`${this.baseApiUrl}/utilization/utilization_snapshots/`, _queryParams ? _queryParams : undefined)
  }

  getVehicleUtilizationStatistics(queryParams?: IQueryParameters): Observable<IUtilisationStatisticsResponse> {
    return this.rest.get<IUtilisationStatisticsResponse>(`${this.baseApiUrl}/utilization/utilization_statistics/`, queryParams)
  }

  getVehicleUtilizationReasonStatistics(queryParams?: IQueryParameters): Observable<any> {
    return this.rest.get<any>(`${this.baseApiUrl}/utilization/unutilized_reason_statistics/`, queryParams)
  }

  getUtilizationEventReasons(): Observable<IEventExceptionReasons[]> {
    return this.rest.get<IEventExceptionReasons[]>(`${this.baseApiUrl}/utilization/manual_utilization_change_reasons/`)
  }

  getVehicleUtilizationEvents(snapshotId: number) {
    return this.rest.get<IUtilisationEventResponse>(`${this.baseApiUrl}/utilization/utilization_events/?snapshot_id=${snapshotId}`)
  }

  getVehicleActivityById(activityId: string): Observable<IVehicleActivityDetail> {
    return this.rest.get<any>(`${this.baseApiUrl}/vehicle_activities/${activityId}/`);
  }

  getScheduledActivityById(activityId: string): Observable<IScheduledActivityDetail> {
    return this.rest.get<any>(`${this.baseApiUrl}/scheduled_activities/${activityId}/`);
  }

  addUtilizationEvent(snapShotId: number, utilization_change_type: number, notes: string): Observable<IUtilisationEvent> {
    const body = {
      utilization_snapshot: snapShotId,
      reference_object_type: "MN",
      notes,
      metadata: {},
      utilization_change_type
    }

    return this.rest.post<IUtilisationEvent>(`${this.baseApiUrl}/utilization/utilization_events/`, body)
  }

  refactorVehicleStatsDataForGraphs(data: IUtilisationStatisticsResponse) {
    // This needs to be for current week.
    const weeklyChartData = {
      labels: [] as string[],
      datasets: [
        {
          label: "Available Percent",
          backgroundColor: colors.available, // Light yellow for Available
          data: [] as number[]
        },
        {
          label: "Unused Percent",
          backgroundColor: colors.unutilized, // Red for Not Available
          data: []
        },
        {
          label: "Used Percent",
          backgroundColor: colors.utilized,
          data: [],
        }
      ]
    }

    const vehicleUtilizationData = {
      labels: ["Unused", "Used", "Available",],
      datasets: [
          {
            data: [] as number[], // Sample data values for each category
            backgroundColor: [colors.unutilized, colors.utilized, colors.available], // Corresponding colors for VOR, In Use, Available
            hoverOffset: 4
          }
        ]
    }

    function convertToPercent (statCount: number, totalCount: number) {
      return (statCount / totalCount) * 100
    }

    let totalStatCounts = {
      unutilizedCount: 0,
      utilizedCount: 0,
      availableCount: 0,
      totalCount: 0,
    }
    function addStatToTotalCounts (dataNode: any) {
      totalStatCounts.unutilizedCount += dataNode.not_available_count;
      totalStatCounts.utilizedCount += dataNode.utilized_count;
      totalStatCounts.availableCount += dataNode.available_count;
      totalStatCounts.totalCount += dataNode.total_count;
    }

    for (const key of Object.keys(data)) {
      const dataNode = (data)[key]

      weeklyChartData.labels.push(key)

      weeklyChartData.datasets[0].data.push(convertToPercent(dataNode.available_count, dataNode.total_count))
      weeklyChartData.datasets[1].data.push(convertToPercent(dataNode.not_available_count, dataNode.total_count))
      weeklyChartData.datasets[2].data.push(convertToPercent(dataNode.utilized_count, dataNode.total_count))

      addStatToTotalCounts(dataNode);
      
    }
    vehicleUtilizationData.datasets[0].data[0] = convertToPercent(totalStatCounts.unutilizedCount, totalStatCounts.totalCount)
    vehicleUtilizationData.datasets[0].data[1] = convertToPercent(totalStatCounts.utilizedCount, totalStatCounts.totalCount)
    vehicleUtilizationData.datasets[0].data[2] = convertToPercent(totalStatCounts.availableCount, totalStatCounts.totalCount)

    return { weeklyChartData, vehicleUtilizationData }
  }

}
